import { useEffect, useRef, useState } from "react";

const BG = "#F8F4EE";
const BG_CARD = "#FFFFFF";
const GOLD = "#C09A5B";
const TAUPE = "#3D2E20";
const MUTED = "#8B7361";
const BORDER = "#D8C9B8";
const VERT = "#5C8A5C";
const ROUGE = "#B85C3A";
const ORANGE = "#D68910";

const FF_PLAYFAIR = "'Playfair Display', serif";
const FF_DM = "'DM Sans', sans-serif";

export default function MultiVillaSimulationPage() {
  const [nomProjet, setNomProjet] = useState("");
  const [nbUnites, setNbUnites] = useState("");
  const [prixUnitaire, setPrixUnitaire] = useState("");
  const [coutTerrain, setCoutTerrain] = useState("");
  const [coutConstruction, setCoutConstruction] = useState("");
  const [fraisDivers, setFraisDivers] = useState("");

  const [companyName, setCompanyName] = useState("MON CABINET");
  const resultsRef = useRef(null);

  useEffect(() => {
    try {
      const n = localStorage.getItem("yoma_company_name");
      if (n) setCompanyName(n);
    } catch (_) {
      /* ignore */
    }
  }, []);

  function toNumber(value) {
    return Number(value || 0);
  }

  function formatMoney(value) {
    return Number(value || 0).toLocaleString("fr-FR") + " MAD";
  }

  const unites = toNumber(nbUnites);
  const venteUnitaire = toNumber(prixUnitaire);
  const terrain = toNumber(coutTerrain);
  const construction = toNumber(coutConstruction);
  const frais = toNumber(fraisDivers);

  const chiffreAffairesTotal = unites * venteUnitaire;
  const coutTotal = terrain + construction + frais;
  const beneficeTotal = chiffreAffairesTotal - coutTotal;
  const marge = chiffreAffairesTotal > 0 ? (beneficeTotal / chiffreAffairesTotal) * 100 : 0;
  const beneficeMoyen = unites > 0 ? beneficeTotal / unites : 0;

  function getStatus(margeValue) {
    if (margeValue < 0) return "Non rentable";
    if (margeValue < 10) return "Faible";
    if (margeValue < 20) return "Correct";
    return "Excellent";
  }

  function getStatusBadgeStyle(status) {
    if (status === "Excellent") {
      return { background: "rgba(92,138,92,0.14)", color: VERT, border: `1px solid rgba(92,138,92,0.4)` };
    }
    if (status === "Correct") {
      return { background: "rgba(214,137,16,0.14)", color: ORANGE, border: `1px solid rgba(214,137,16,0.45)` };
    }
    if (status === "Faible") {
      return { background: "rgba(184,92,58,0.12)", color: ROUGE, border: `1px solid rgba(184,92,58,0.35)` };
    }
    return { background: "rgba(184,92,58,0.15)", color: ROUGE, border: `1px solid rgba(184,92,58,0.4)` };
  }

  const status = getStatus(marge);

  const inputBase = {
    width: "100%",
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    padding: "12px 14px",
    background: BG_CARD,
    fontFamily: FF_DM,
    fontSize: 14,
    color: TAUPE,
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontWeight: 600,
    color: MUTED,
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  function scrollToResults() {
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  const margeColor = marge >= 0 ? VERT : ROUGE;

  return (
    <><style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;600;700&display=swap');
        `}
      </style><main
        style={{
          minHeight: "100%",
          background: BG,
          fontFamily: FF_DM,
          color: TAUPE,
          padding: "28px 24px 48px",
          boxSizing: "border-box",
        }}
      ><div style={{ maxWidth: 1100, margin: "0 auto" }}><header style={{ marginBottom: 28 }}><div
              style={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: MUTED,
                marginBottom: 8,
              }}
            >
              {companyName}
            </div><h1
              style={{
                margin: 0,
                fontFamily: FF_PLAYFAIR,
                fontSize: 32,
                fontWeight: 700,
                color: TAUPE,
                lineHeight: 1.15,
              }}
            >
              Simulation Multi-Projets
            </h1><p style={{ margin: "10px 0 0", fontSize: 15, color: MUTED, maxWidth: 520 }}>
              Étude rapide de rentabilité pour plusieurs unités
            </p></header><div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
              gap: 24,
              alignItems: "stretch",
            }}
            className="multi-projets-grid"
          ><style>{`
              @media (max-width: 900px) {
                .multi-projets-grid { grid-template-columns: 1fr !important; }
              }
            `}</style>

            {/* Colonne gauche — Paramètres */}
            <section
              style={{
                background: BG_CARD,
                borderRadius: 16,
                padding: "22px 24px 24px",
                border: `1px solid ${BORDER}`,
                boxShadow: "0 6px 28px rgba(61,46,32,0.07)",
                display: "flex",
                flexDirection: "column",
              }}
            ><h2 style={{ margin: "0 0 18px", fontFamily: FF_PLAYFAIR, fontSize: 22, fontWeight: 700, color: TAUPE }}>Paramètres du projet</h2><div style={{ display: "flex", flexDirection: "column", gap: 14, flex: 1 }}><div><label style={labelStyle}>Nom du projet</label><input placeholder="Nom du projet" value={nomProjet} onChange={(e) => setNomProjet(e.target.value)} style={inputBase} /></div><div><label style={labelStyle}>Nombre d&apos;unités</label><input
                    placeholder="Nombre d’unités / projets"
                    value={nbUnites}
                    onChange={(e) => setNbUnites(e.target.value)}
                    style={inputBase}
                  /></div><div><label style={labelStyle}>Prix de vente unitaire (MAD)</label><input placeholder="0" value={prixUnitaire} onChange={(e) => setPrixUnitaire(e.target.value)} style={inputBase} /></div><div><label style={labelStyle}>Coût terrain global (MAD)</label><input placeholder="0" value={coutTerrain} onChange={(e) => setCoutTerrain(e.target.value)} style={inputBase} /></div><div><label style={labelStyle}>Coût construction global (MAD)</label><input placeholder="0" value={coutConstruction} onChange={(e) => setCoutConstruction(e.target.value)} style={inputBase} /></div><div><label style={labelStyle}>Frais divers globaux (MAD)</label><input placeholder="0" value={fraisDivers} onChange={(e) => setFraisDivers(e.target.value)} style={inputBase} /></div></div><button
                type="button"
                onClick={scrollToResults}
                style={{
                  marginTop: 22,
                  width: "100%",
                  padding: "14px 20px",
                  borderRadius: 12,
                  border: "none",
                  background: `linear-gradient(135deg, ${GOLD} 0%, #A8844A 100%)`,
                  color: "#FFF",
                  fontFamily: FF_DM,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 4px 18px rgba(192,154,91,0.45)",
                }}
              >
                Calculer
              </button></section>

            {/* Colonne droite — Résultats */}
            <section
              ref={resultsRef}
              style={{
                background: BG_CARD,
                borderRadius: 16,
                padding: "24px 26px 26px",
                border: `1px solid rgba(192,154,91,0.35)`,
                boxShadow: "0 12px 40px rgba(61,46,32,0.1), inset 0 1px 0 rgba(255,255,255,0.8)",
                display: "flex",
                flexDirection: "column",
                minHeight: 360,
              }}
            ><h2
                style={{
                  margin: "0 0 6px",
                  fontFamily: FF_PLAYFAIR,
                  fontSize: 20,
                  fontWeight: 700,
                  color: TAUPE,
                  lineHeight: 1.3,
                }}
              >
                {nomProjet?.trim() ? nomProjet : "Projet sans nom"}
              </h2><p style={{ margin: "0 0 20px", fontSize: 12, color: MUTED }}>Synthèse financière multi-projets</p><div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 14,
                  marginBottom: 20,
                }}
              ><div
                  style={{
                    borderRadius: 14,
                    padding: "16px 18px",
                    background: "linear-gradient(145deg, rgba(192,154,91,0.12) 0%, rgba(192,154,91,0.05) 100%)",
                    border: `1px solid rgba(192,154,91,0.3)`,
                  }}
                ><div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, letterSpacing: "0.04em" }}>CA total (MAD)</div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 26, fontWeight: 700, color: GOLD, lineHeight: 1.1 }}>{formatMoney(chiffreAffairesTotal)}</div></div><div
                  style={{
                    borderRadius: 14,
                    padding: "16px 18px",
                    background: "rgba(61,46,32,0.04)",
                    border: `1px solid ${BORDER}`,
                  }}
                ><div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, letterSpacing: "0.04em" }}>Coût total (MAD)</div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 24, fontWeight: 700, color: TAUPE, lineHeight: 1.1 }}>{formatMoney(coutTotal)}</div></div><div
                  style={{
                    borderRadius: 14,
                    padding: "16px 18px",
                    background: beneficeTotal >= 0 ? "rgba(92,138,92,0.08)" : "rgba(184,92,58,0.08)",
                    border: `1px solid ${beneficeTotal >= 0 ? "rgba(92,138,92,0.3)" : "rgba(184,92,58,0.3)"}`,
                  }}
                ><div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, letterSpacing: "0.04em" }}>Bénéfice total (MAD)</div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 24, fontWeight: 700, color: beneficeTotal >= 0 ? VERT : ROUGE, lineHeight: 1.1 }}>
                    {formatMoney(beneficeTotal)}
                  </div></div><div
                  style={{
                    borderRadius: 14,
                    padding: "16px 18px",
                    background: "rgba(61,46,32,0.03)",
                    border: `1px solid ${BORDER}`,
                  }}
                ><div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, letterSpacing: "0.04em" }}>Marge globale (%)</div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 28, fontWeight: 700, color: margeColor, lineHeight: 1.1 }}>{marge.toFixed(1)}%</div></div></div><div
                style={{
                  padding: "14px 16px",
                  borderRadius: 12,
                  background: BG,
                  border: `1px dashed ${BORDER}`,
                  marginBottom: 18,
                }}
              ><div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Bénéfice moyen par unité
                </div><div style={{ fontFamily: FF_PLAYFAIR, fontSize: 20, fontWeight: 700, color: TAUPE }}>{formatMoney(beneficeMoyen)}</div></div><div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}><span style={{ fontSize: 12, fontWeight: 600, color: MUTED }}>Diagnostic</span><span
                  style={{
                    display: "inline-block",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    padding: "8px 14px",
                    borderRadius: 999,
                    fontFamily: FF_DM,
                    ...getStatusBadgeStyle(status),
                  }}
                >
                  {status}
                </span></div></section></div></div></main></>
  );
}
